import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import { getAuth, connectAuthEmulator } from "firebase/auth";
import { initializeFirestore, connectFirestoreEmulator, setLogLevel } from "firebase/firestore";
import { getFunctions, connectFunctionsEmulator } from "firebase/functions";
import { getStorage, connectStorageEmulator } from "firebase/storage";

// Dynamic Organization ID (set at runtime)
export let ORGANIZATION_ID: string = "";

export const setOrganizationId = (orgId: string) => {
  ORGANIZATION_ID = orgId;
};

/**
 * The selected organization, or a throw when none is selected. Multi-step
 * operations call this ONCE at entry and thread the value through, so a
 * mid-flight organization switch cannot redirect the rest of the operation.
 */
export const requireOrganizationId = (): string => {
  if (!ORGANIZATION_ID) throw new Error("No organization selected");
  return ORGANIZATION_ID;
};

// Use different project ID for emulators vs production
const isEmulatorMode = import.meta.env.DEV && import.meta.env.VITE_USE_PRODUCTION !== 'true';

const firebaseConfig = isEmulatorMode
  ? {
      apiKey: "fake-api-key",
      authDomain: "glean-ag.firebaseapp.com",
      projectId: "demo-test",
      storageBucket: "glean-ag.firebasestorage.app", // emulators accept any name; keep current for test compat
    }
  : {
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
      storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
      messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
      appId: import.meta.env.VITE_FIREBASE_APP_ID,
      measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    };

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize services
export const db = initializeFirestore(app, {
  ignoreUndefinedProperties: true,
});
export const auth = getAuth(app);
// Only initialize Analytics in real browser environments with a measurement ID.
// Skip in: emulator mode (no measurement ID configured), vitest (jsdom tears down
// before Analytics' async gtag loader finishes, causing "ReferenceError: window is
// not defined" in CI), and any build lacking VITE_FIREBASE_MEASUREMENT_ID.
const isTestEnv = import.meta.env.MODE === 'test';
export const analytics =
  !isEmulatorMode &&
  !isTestEnv &&
  typeof window !== 'undefined' &&
  import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
    ? getAnalytics(app)
    : null;
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Connect to Firebase Emulators in development mode (unless explicitly disabled)
if (import.meta.env.DEV && import.meta.env.VITE_USE_PRODUCTION !== 'true') {
  const firestorePort = parseInt(import.meta.env.VITE_FIRESTORE_PORT || '8100', 10);
  const authPort = parseInt(import.meta.env.VITE_AUTH_PORT || '9099', 10);
  const storagePort = parseInt(import.meta.env.VITE_STORAGE_PORT || '9199', 10);
  const functionsPort = parseInt(import.meta.env.VITE_FUNCTIONS_PORT || '5001', 10);
  connectFirestoreEmulator(db, 'localhost', firestorePort);
  connectAuthEmulator(auth, `http://localhost:${authPort}`, { disableWarnings: true });
  connectStorageEmulator(storage, 'localhost', storagePort);
  connectFunctionsEmulator(functions, 'localhost', functionsPort);
  console.log('🔧 Connected to Firebase Emulators');
  console.log('   Project ID: demo-test');
  console.log(`   Firestore: localhost:${firestorePort}`);
  console.log(`   Auth: localhost:${authPort}`);
  console.log(`   Storage: localhost:${storagePort}`);
  console.log(`   Functions: localhost:${functionsPort}`);
} else {
  const envName = import.meta.env.MODE === 'staging' ? 'Staging' : 'Production';
  console.log(`🌐 Connected to ${envName} Firebase`);
  console.log(`   Project ID: ${import.meta.env.VITE_FIREBASE_PROJECT_ID}`);
}

// Opt-in Firestore SDK debug logging for diagnosing hung reads/writes
// (WebChannel stream state, auth token refresh failures, retry backoff).
// Enable in the browser console with:  localStorage.GLEAN_FIRESTORE_DEBUG = "1"
// then refresh. Disable by removing the key.
if (
  import.meta.env.DEV &&
  typeof window !== "undefined" &&
  typeof window.localStorage?.getItem === "function" &&
  window.localStorage.getItem("GLEAN_FIRESTORE_DEBUG") === "1"
) {
  setLogLevel("debug");
  console.warn("🔍 Firestore debug logging ENABLED (localStorage.GLEAN_FIRESTORE_DEBUG)");
}

// Test that Firebase is initialized correctly
console.log("Firebase initialized successfully");
